// client/src/components/doctors/PatientManagement/PatientList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import doctorService from '../../../services/doctorService';
import '../../../styles/components/doctor/PatientList.css';

import DoctorStatusGuard, { StatusBanner, RestrictedFeatureMessage } from '../../common/DoctorStatusGuard';
import '../../../styles/components/doctor/DoctorStatus.css';

import AddPatientModal from './AddPatientModal';
import exportPdfIcon from '../../../assets/logo/pdf_icon.svg'; 

import neurolexLogo from '../../../assets/images/Neurolex_Logo_New.png';

const PatientList = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [appointmentFilter, setAppointmentFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  
  // NEW: Rows per page state
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // End Care Modal States
  const [showEndCareModal, setShowEndCareModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [endCareLoading, setEndCareLoading] = useState(false);
  const [understanding, setUnderstanding] = useState(false);

  const [showAddPatientModal, setShowAddPatientModal] = useState(false);

  const [user, setUser] = useState(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [appointmentDropdownOpen, setAppointmentDropdownOpen] = useState(false);
  const [showDropdownOpen, setShowDropdownOpen] = useState(false);

  const statusOptions = [
    { value: 'All', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Pending', label: 'Pending' }
  ];

  const appointmentOptions = [
    { value: 'All', label: 'All' },
    { value: 'Today', label: 'Today' },
    { value: 'This Week', label: 'This Week' },
    { value: 'This Month', label: 'This Month' },
    { value: 'No Appointments', label: 'No Appointments' }
  ];

  const rowsOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' }
  ];

  const fetchPatients = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found in localStorage');
        setError('Authentication error: No token found. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await doctorService.getPatients();
      
      if (response.success && response.patients) {
        console.log(`✅ Loaded ${response.patients.length} patients for this doctor`);
        setPatients(response.patients);
        setFilteredPatients(response.patients);
      } else if (response.data && response.data.success && response.data.patients) {
        console.log(`✅ Loaded ${response.data.patients.length} patients for this doctor`);
        setPatients(response.data.patients);
        setFilteredPatients(response.data.patients);
      } else if (Array.isArray(response)) {
        console.log(`✅ Loaded ${response.length} patients for this doctor (array format)`);
        setPatients(response);
        setFilteredPatients(response);
      } else if (Array.isArray(response.data)) {
        console.log(`✅ Loaded ${response.data.length} patients for this doctor (array format in data)`);
        setPatients(response.data);
        setFilteredPatients(response.data);
      } else {
        setError('Failed to load patients: Unexpected response format');
        console.error('Unexpected response format:', response);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patients:', error);
      
      let errorMessage = 'Failed to load patients. Please try again later.';
      
      if (error.response) {
        console.error('Error response:', error.response.status, error.response.data);
        
        if (error.response.status === 401) {
          errorMessage = 'Authentication error: Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied: You do not have permission to view patients.';
        } else {
          errorMessage = `Error: ${error.response.status} - ${error.response.data.message || 'Unknown error'}`;
        }
      }
      
      setError(errorMessage);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock patient data for development');
        const mockPatients = [
          { _id: '1', firstName: 'John', lastName: 'Doe', email: 'john@example.com', gender: 'Male', age: 42, status: 'Active' },
          { _id: '2', firstName: 'Jane', lastName: 'Smith', email: 'jane@example.com', gender: 'Female', age: 35, status: 'Active' },
          { _id:'3', firstName: 'Alex', lastName: 'Brown', email: 'alex@example.com', gender: 'Other', age: 28, status: 'Inactive' },
        ];
        setPatients(mockPatients);
        setFilteredPatients(mockPatients);
      }
      
      setLoading(false);
    }
  };

  const loadUserData = () => {
    try {
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        console.log('🔒 [PatientList StatusGuard] User loaded:', {
          accountStatus: userObj.accountStatus,
          verificationStatus: userObj.verificationStatus,
          role: userObj.role
        });
      }
    } catch (error) {
      console.error('❌ [PatientList StatusGuard] Error loading user:', error);
    }
  };

  useEffect(() => {
    loadUserData(); 
    fetchPatients();
  }, []);
  
  // Handle search and filters
  useEffect(() => {
    let filtered = patients;
    
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(patient => 
        patient.firstName?.toLowerCase().includes(query) || 
        patient.lastName?.toLowerCase().includes(query) || 
        patient.email?.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== 'All') {
      filtered = filtered.filter(patient => patient.status === statusFilter);
    }
    
    if (appointmentFilter !== 'All') {
      // Placeholder for appointment filter logic
    }
    
    setFilteredPatients(filtered);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, appointmentFilter, patients]);
  
  // NEW: Reset to first page when rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);
  
  // Pagination calculations
  const indexOfLastPatient = currentPage * rowsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - rowsPerPage;
  const currentPatients = filteredPatients.slice(indexOfFirstPatient, indexOfLastPatient);
  const totalPages = Math.ceil(filteredPatients.length / rowsPerPage);
  
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Handle actions
  const handleViewPatient = (patientId) => {
    navigate(`/doctor/patients/${patientId}`);
  };
  
  const handleEndCare = (patient) => {
    console.log('Opening end care modal for patient:', patient);
    setSelectedPatient(patient);
    setShowEndCareModal(true);
    setUnderstanding(false);
  };
  
  const closeEndCareModal = () => {
    setShowEndCareModal(false);
    setSelectedPatient(null);
    setUnderstanding(false);
    setEndCareLoading(false);
  };
  
  const confirmEndCare = async () => {
    if (!understanding || !selectedPatient) {
      return;
    }
    
    try {
      setEndCareLoading(true);
      
      const response = await doctorService.endPatientCare(selectedPatient._id);
      
      if (response.success) {
        setPatients(prev => prev.filter(p => p._id !== selectedPatient._id));
        setFilteredPatients(prev => prev.filter(p => p._id !== selectedPatient._id));
        
        console.log('Care ended successfully:', response.message);
        closeEndCareModal();
      } else {
        throw new Error(response.message || 'Failed to end care');
      }
    } catch (error) {
      console.error('Error ending care:', error);
      setError(error.message || 'Failed to end care relationship');
      setEndCareLoading(false);
    }
  };
  
  const handleAddPatient = () => {
    setShowAddPatientModal(true);
  };

  const handlePatientsAdded = (addedPatients) => {
    console.log('✅ Patients added:', addedPatients);
    fetchPatients();
  };

  const handleExportPDF = async () => {
    try {
      console.log('📄 Starting PDF export...');
      
      setLoading(true);
      
      const exportFilters = {
        search: searchQuery,
        status: statusFilter,
        appointment: appointmentFilter,
        rowsPerPage: 'all'
      };
      
      console.log('📋 Export filters:', exportFilters);
      
      const response = await doctorService.exportPatientListToPDF(exportFilters);
      
      if (response.isHtml) {
        console.log('📄 Opening HTML export in new tab...');
        
        const htmlUrl = URL.createObjectURL(response.data);
        const newWindow = window.open(htmlUrl, '_blank');
        
        if (newWindow) {
          newWindow.focus();
          
          setTimeout(() => {
            URL.revokeObjectURL(htmlUrl);
          }, 10000);
          
          alert(`✅ Patient list exported successfully!\n\n📊 ${response.patientCount} patients included in the export.\n\n💡 Use Ctrl+P in the new tab to save as PDF.`);
        } else {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }
      } else {
        console.log('📄 Processing PDF download...');
        
        const pdfUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `neurolex-patient-list-${new Date().toISOString().split('T')[0]}.pdf`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 10000);
        
        alert('✅ Patient list PDF downloaded successfully!');
      }
      
    } catch (error) {
      console.error('❌ PDF export failed:', error);
      
      let errorMessage = 'Failed to export patient list. ';
      
      if (error.message.includes('Popup blocked')) {
        errorMessage += 'Please allow popups for this site and try again.';
      } else if (error.message.includes('network')) {
        errorMessage += 'Please check your internet connection and try again.';
      } else {
        errorMessage += 'Please try again later.';
      }
      
      alert(`❌ Export Error\n\n${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };
  
  const formatPatientName = (patient) => {
    if (!patient) return 'Unknown Patient';
    return `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.email || 'Unknown Patient';
  };
  
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
  };
  
  // ✅ LOADING STATE - Separate return, NO guard wrapping
  if (loading) {
    return (
      <div className="neurolex-loading-container">
        <div className="neurolex-loading-insideContainer">
          <div className="neurolex-dashboard-spinner">
            <div className="spinner-circle outer"></div>
            <div className="spinner-circle middle"></div>
            <div className="spinner-circle inner"></div>
            <div className="spinner-center">
              <img 
                src={neurolexLogo} 
                alt="NeuroLex Logo" 
                className="loading-logo"
                onError={(e) => {
                  console.warn('❌ Logo failed to load from:', neurolexLogo);
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    fallback.style.display = 'flex';
                    fallback.style.alignItems = 'center';
                    fallback.style.justifyContent = 'center';
                  }
                }}
                onLoad={() => {
                  console.log('✅ NeuroLex logo loaded successfully:', neurolexLogo);
                }}
              />
              <div className="loading-logo-fallback" style={{ display: 'none' }}>
                👥
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ MAIN RETURN - Wrapped with guard
  return (
    <>
      {/* ✅ NEW: Status Banner */}
      <StatusBanner user={user} />
      
      {/* ✅ NEW: Wrap everything in DoctorStatusGuard */}
      <DoctorStatusGuard 
        user={user} 
        requiredStatus="approved"
        fallback={
          <div className="patient-list-restricted">
            <div className="doc-patient-list-page-header">
              <div className="header-content">
                <div className="header-text">
                  <h1 className="dashboard-title">My Patients</h1>
                  <p className="dashboard-subtitle">Compassionate care begins here. View and manage the individuals you're supporting.</p>
                </div>
                <div className="profile-box">
                  <img src="/src/assets/images/patienticon.png" alt="Patient Icon" className="profile-icon" />
                </div>
              </div>
            </div>
            
            <div className="section-divider"></div>
            
            <RestrictedFeatureMessage 
              title="Patient Management"
              message="Access to patient records is available after your account is verified."
            />
          </div>
        }
      >
        {/* Header with Profile Box */}
        <div className="doc-patient-list-page-header">
          <div className="header-content">
            <div className="header-text">
              <h1 className="dashboard-title">My Patients</h1>
              <p className="dashboard-subtitle">Compassionate care begins here. View and manage the individuals you're supporting.</p>
            </div>
            <div className="profile-box">
              <img src="/src/assets/images/patienticon.png" alt="Patient Icon" className="profile-icon" />
            </div>
          </div>
        </div>
        
        <div className="section-divider"></div>
        
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{error}</span>
          </div>
        )}
        
        {/* UPDATED: New layout structure following exact specifications */}
        <div className="list-controls">
          {/* TOP ROW: Search + Add Patient (left) | Show + Export (right) */}
          <div className="top-controls-row">
            {/* LEFT: Search and Add Patient with 10px gap */}
            <div className="search-add-group">
              <div className="search-container">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <svg 
                    className="search-icon" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" 
                      stroke="#548170" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              
              <button
                className="primary-button"
                onClick={handleAddPatient}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19M5 12H19" stroke="#ffffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add a Patient
              </button>
            </div>
            
            {/* RIGHT: Show and Export stacked vertically at right edge */}
            <div className="show-export-group">
              <div className="show-entries">
                <label className="show-label">Show</label>
                <div className="custom-show-dropdown">
                  <div
                    className="custom-show-trigger"
                    onClick={() => setShowDropdownOpen(!showDropdownOpen)}
                  >
                    <span>{rowsPerPage}</span>
                    <svg 
                      className={`dropdown-arrow ${showDropdownOpen ? 'open' : ''}`}
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path 
                        d="M6 9l6 6 6-6" 
                        stroke="#548170" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  
                  {showDropdownOpen && (
                    <div className="custom-show-options">
                      {rowsOptions.map(option => (
                        <div
                          key={option.value}
                          className={`custom-show-option ${rowsPerPage === option.value ? 'selected' : ''}`}
                          onClick={() => {
                            setRowsPerPage(option.value);
                            setShowDropdownOpen(false);
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <button
                className="export-button"
                onClick={handleExportPDF}
              >
                <img 
                  src={exportPdfIcon} 
                  alt="Export" 
                  width="16" 
                  height="16"
                />
                Export PDF
              </button>
            </div>
          </div>
          
          {/* BOTTOM ROW: Filter Controls */}
          <div className="filter-controls">
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <div className="custom-filter-dropdown">
                <div
                  className="custom-filter-trigger"
                  onClick={() => {
                    setStatusDropdownOpen(!statusDropdownOpen);
                    setAppointmentDropdownOpen(false);
                  }}
                >
                  <span>{statusFilter}</span>
                  <svg 
                    className={`dropdown-arrow ${statusDropdownOpen ? 'open' : ''}`}
                    viewBox="0 0 24 24" 
                    fill="none"
                  >
                    <path 
                      d="M6 9l6 6 6-6" 
                      stroke="#548170" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                
                {statusDropdownOpen && (
                  <div className="custom-filter-options">
                    {statusOptions.map(option => (
                      <div
                        key={option.value}
                        className={`custom-filter-option ${statusFilter === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          setStatusFilter(option.value);
                          setStatusDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="filter-group">
              <label className="filter-label">Upcoming Appointments</label>
              <div className="custom-filter-dropdown">
                <div
                  className="custom-filter-trigger"
                  onClick={() => {
                    setAppointmentDropdownOpen(!appointmentDropdownOpen);
                    setStatusDropdownOpen(false);
                  }}
                >
                  <span>{appointmentFilter}</span>
                  <svg 
                    className={`dropdown-arrow ${appointmentDropdownOpen ? 'open' : ''}`}
                    viewBox="0 0 24 24" 
                    fill="none"
                  >
                    <path 
                      d="M6 9l6 6 6-6" 
                      stroke="#548170" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                
                {appointmentDropdownOpen && (
                  <div className="custom-filter-options">
                    {appointmentOptions.map(option => (
                      <div
                        key={option.value}
                        className={`custom-filter-option ${appointmentFilter === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          setAppointmentFilter(option.value);
                          setAppointmentDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="patient-management-dashboard-section">
          <div className="patient-management-table">
            {/* Table Header */}
            <div className="patient-management-table-row patient-management-header-row">
              <div className="patient-management-table-cell">Patient</div>
              <div className="patient-management-table-cell">Email</div>
              <div className="patient-management-table-cell">Gender</div>
              <div className="patient-management-table-cell">Age</div>
              <div className="patient-management-table-cell">Actions</div>
            </div>
            
            {/* Table Body */}
            {filteredPatients.length === 0 ? (
              <div className="patient-management-table-row patient-management-empty-row">
                <div className="patient-management-table-cell" style={{ gridColumn: '1 / -1' }}>
                  <p className="patient-management-no-data">No patients found</p>
                </div>
              </div>
            ) : (
              currentPatients.map((patient, index) => (
                <div 
                  key={patient._id} 
                  className="patient-management-table-row"
                >
                  <div className="patient-management-table-cell patient-management-patient-cell">
                    <div className="patient-management-avatar">
                      {patient.profilePicture ? (
                        <img 
                          src={patient.profilePicture} 
                          alt={`${patient.firstName} ${patient.lastName}`}
                          className="patient-avatar-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.textContent = patient.firstName?.[0] || 'P';
                          }}
                        />
                      ) : (
                        patient.firstName?.[0] || 'P'
                      )}
                    </div>
                    <div className="patient-management-name">
                      {`${patient.firstName || ''} ${patient.lastName || ''}`}
                    </div>
                  </div>
                  
                  <div className="patient-management-table-cell">{patient.email}</div>
                  <div className="patient-management-table-cell">{patient.gender || 'Not specified'}</div>
                  <div className="patient-management-table-cell">{patient.age || 'Unknown'}</div>
                  
                  <div className="patient-management-table-cell patient-management-actions-cell">
                    <div className="patient-management-action-buttons">
                      <button 
                        className="patient-management-btn-icon patient-management-btn-view"
                        onClick={() => handleViewPatient(patient._id)}
                        title="View patient details"
                      >
                        <svg className="patient-management-icon-view" viewBox="0 0 24 24">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                      
                      <button
                        className="patient-management-btn-icon patient-management-btn-delete"
                        onClick={() => handleEndCare(patient)}
                        title="End care relationship"
                      >
                        <svg className="patient-management-icon-delete" viewBox="0 0 24 24">
                          <path d="M18 6L6 18"/>
                          <path d="M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Enhanced Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="page-nav prev"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    className={`page-number ${currentPage === index + 1 ? 'active' : ''}`}
                    onClick={() => paginate(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              
              <button 
                className="page-nav next"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
        
      </DoctorStatusGuard>
      {/* ✅ GUARD CLOSED - Modals stay outside */}
      
      {/* END CARE MODAL */}
      {showEndCareModal && (
        <div className="modal-overlay">
          <div className="modal-container end-care-modal">
            <div className="modal-header">
              <div className="header-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="modal-title">END CARE FOR THIS PATIENT?</h2>
            </div>

            <div className="patient-info-card">
              <div className="patient-name">{formatPatientName(selectedPatient)}</div>
              <div className="patient-subtitle">Mood Check-In Log - April 19, 2025</div>
            </div>

            <div className="warning-message">
              <p>Are you sure you want to remove this patient from your care?</p>
              <p>This will end your access to their records and session history.</p>
            </div>

            <div className="understanding-section">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={understanding}
                  onChange={(e) => setUnderstanding(e.target.checked)}
                  className="understanding-checkbox"
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">
                  I understand that ending care will terminate my professional relationship with this patient
                </span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={closeEndCareModal}
                disabled={endCareLoading}
              >
                Cancel
              </button>
              <button
                className={`confirm-button ${!understanding ? 'disabled' : ''}`}
                onClick={confirmEndCare}
                disabled={!understanding || endCareLoading}
              >
                {endCareLoading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Ending Care...
                  </>
                ) : (
                  'Yes, Remove'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showAddPatientModal && (
        <AddPatientModal
          isOpen={showAddPatientModal}
          onClose={() => setShowAddPatientModal(false)}
          onPatientAdded={handlePatientsAdded}
        />
      )}
    </>
  );
};

export default PatientList;